import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useLanguage } from '../contexts/LanguageProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import * as api from '../services/apiService';

const EmailVerifyPage: React.FC = () => {
    const { currentUser, setCurrentUser, resendVerification } = useAuth();
    const { t } = useLanguage();
    const { handleNavigation } = useNavigation();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [resent, setResent] = useState(false);

    useEffect(() => {
        const token = new URLSearchParams(window.location.search).get('token');
        if (!token) {
            setStatus('error');
            return;
        }
        api.verifyEmail(token)
            .then(() => {
                if (currentUser) {
                    setCurrentUser({ ...currentUser, emailVerified: true });
                }
                setStatus('success');
                setTimeout(() => handleNavigation('feed'), 2000);
            })
            .catch(() => setStatus('error'));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleResend = async () => {
        await resendVerification();
        setResent(true);
    };

    return (
        <div className="min-h-screen bg-brand-dark flex flex-col items-center justify-center px-4">
            <div className="mb-10">
                <img src="/logo.png" alt="beYourAMP" className="h-10 w-auto" />
            </div>

            <div className="w-full max-w-md bg-brand-surface rounded-2xl border border-gray-800 p-8 flex flex-col items-center text-center gap-6">
                {status === 'loading' && (
                    <div className="flex flex-col items-center gap-4">
                        <div className="h-10 w-10 border-2 border-brand-orange border-t-transparent rounded-full animate-spin" />
                        <p className="text-brand-muted text-sm">Verifying...</p>
                    </div>
                )}

                {status === 'success' && (
                    <>
                        <div className="h-16 w-16 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center">
                            <svg className="h-8 w-8 text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="text-2xl font-black text-white mb-2">{t('EMAIL_VERIFY_SUCCESS')}</h1>
                            <p className="text-brand-muted text-sm">Redirecting...</p>
                        </div>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="h-16 w-16 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center">
                            <svg className="h-8 w-8 text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="text-xl font-black text-white mb-2">{t('EMAIL_VERIFY_INVALID')}</h1>
                        </div>
                        {resent ? (
                            <p className="text-green-400 text-sm">{t('EMAIL_VERIFY_RESENT')}</p>
                        ) : (
                            <button
                                onClick={handleResend}
                                className="w-full py-3 rounded-xl bg-brand-orange text-white font-bold hover:bg-orange-600 transition-all"
                            >
                                {t('EMAIL_VERIFY_NEW_LINK')}
                            </button>
                        )}
                        <button
                            onClick={() => handleNavigation('feed')}
                            className="text-brand-muted text-sm hover:text-white transition-colors"
                        >
                            {t('NAV_HOME')}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default EmailVerifyPage;
